import { useState } from "react";
import { Link } from "react-router-dom";
import { Gem, Layers, Route } from "lucide-react";
import { MonoLabel, Em, Card } from "@/site/components/Atoms";
import PipelineH from "@/site/anim/PipelineH";
import NodeCluster from "@/site/anim/NodeCluster";
import { INSIGHTS } from "@/site/pages/insightsContent";

export default function Home() {
  return (
    <>
      <Hero />
      <WhatWeDo />
      <KosovoSection />
      <SectorsSection />
      <NetworkPreview />
      <ExperienceSection />
      <InsightsPreview />
      <FinalCTA />
    </>
  );
}

/* === 1 · HERO (no kicker — removed to avoid repetition with section 2) === */
function Hero() {
  return (
    <section className="relative max-w-[1500px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28">
      <h1 className="h-display fade-up text-[44px] sm:text-[58px] lg:text-[78px] max-w-5xl">
        Turning market opportunities <br className="hidden md:block" />
        into <Em>operational reality.</Em>
      </h1>
      <p className="fade-up delay-1 mt-7 max-w-2xl text-[15px] leading-[1.85] text-white/65">
        Forealco helps companies transform cross-border market potential into structured,
        locally grounded, and commercially viable business routes — connecting opportunity
        insight with execution discipline.
      </p>
      <div className="fade-up delay-2 mt-10 flex flex-wrap items-center gap-3">
        <Link to="/explore-opportunities" data-testid="hero-cta-primary" className="btn-primary">
          Explore Opportunities <span>→</span>
        </Link>
        <Link to="/approach" data-testid="hero-cta-secondary" className="btn-secondary">
          See Our Approach
        </Link>
      </div>
    </section>
  );
}

/* === 2 · WHAT FOREALCO DOES — unified with the four-stage animation === */
function WhatWeDo() {
  const [hover, setHover] = useState(null);
  return (
    <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-28 lg:mt-36">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="max-w-3xl">
          <MonoLabel>WHAT FOREALCO DOES</MonoLabel>
          <h2 className="h-display mt-4 text-[34px] md:text-[44px]">
            Forealco turns market interest <Em>into structured business action.</Em>
          </h2>
          <p className="mt-6 text-[14.5px] leading-[1.85] text-white/65 max-w-2xl">
            Forealco is designed for companies that need more than a market report, a company
            formation service, or a local introduction. It helps test whether an opportunity can
            become a credible route, then shapes the structure, partners, and execution layers
            needed to move forward.
          </p>
        </div>
        <div className="text-[11px] tracking-[0.3em] uppercase text-white/45 hidden md:block">
          FOUR STAGES · ONE PATH
        </div>
      </div>

      <div className="mt-12 grid grid-cols-4 gap-0 mb-3">
        {["01 · Discover", "02 · Structure", "03 · Coordinate", "04 · Realize"].map((s) => (
          <MonoLabel key={s} color="rgba(232,236,241,0.55)">{s}</MonoLabel>
        ))}
      </div>
      <PipelineH hoverStage={hover} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 mt-2">
        {[
          ["Discover", "We examine opportunity signals, market conditions, institutional context, commercial logic, partner availability, and practical constraints."],
          ["Structure", "We shape the entry model, operating route, partner roles, commercial path, and operational readiness."],
          ["Coordinate", "We align selected advisory, local, digital, operational, trade, and institutional support layers where required."],
          ["Realize", "We help move the opportunity toward a commercially realistic and operationally grounded route."],
        ].map(([t, d], i) => (
          <div
            key={t}
            data-testid={`stage-${i}`}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            className={`border-t pt-5 pr-6 pb-5 cursor-pointer transition-colors ${
              hover === i ? "border-[#9DB4FF]" : "border-white/15"
            }`}
          >
            <div className="flex items-baseline gap-3">
              <span className="font-mono text-[10px] tracking-[0.3em] text-[#9DB4FF]">0{i + 1}</span>
              <span className="font-display text-[22px]">{t}</span>
            </div>
            <p className="text-[12.5px] mt-2 text-white/60 leading-[1.7]">{d}</p>
          </div>
        ))}
      </div>

      <div className="mt-10">
        <Link to="/approach" data-testid="approach-cta" className="btn-secondary">
          See the full Approach →
        </Link>
      </div>
    </section>
  );
}

/* === 3 · KOSOVO === */
function KosovoSection() {
  return (
    <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-32 grid grid-cols-12 gap-10">
      <div className="col-span-12 lg:col-span-5">
        <MonoLabel>FIRST ACTIVE OPPORTUNITY LANDSCAPE</MonoLabel>
        <h2 className="h-display mt-5 text-[34px] md:text-[42px]">
          Kosovo as a <Em>first active opportunity landscape.</Em>
        </h2>
        <p className="mt-7 text-[14px] leading-[1.85] text-white/70 max-w-md">
          Kosovo is Forealco's first active opportunity landscape, not its limitation. It offers a
          practical environment where market entry, advisory coordination, digital business
          infrastructure, service commercialization, institutional opportunities, and selected
          cross-border business routes can be evaluated together.
        </p>

        <blockquote className="mt-8 pl-5 border-l-2 border-[#9DB4FF] text-[16px] leading-[1.6] text-white/85 font-display italic">
          More than a tax advantage, Kosovo can be approached as a practical base for structured
          opportunity realization.
        </blockquote>

        <p className="mt-6 text-[13px] leading-[1.85] text-white/55 max-w-md">
          Forealco evaluates where Kosovo can create real business value — and where a more
          cautious, phased, or partner-enabled approach is required.
        </p>

        <Link to="/markets" data-testid="kosovo-cta" className="btn-accent mt-10">
          Explore Kosovo Opportunities →
        </Link>
      </div>

      <div className="col-span-12 lg:col-span-7 relative h-[420px] md:h-[540px]">
        <NodeCluster height={540} />
      </div>
    </section>
  );
}

/* === 4 · STRATEGIC OPPORTUNITY AREAS === */
function SectorsSection() {
  const sectors = [
    ["01", "Digital Business Infrastructure", "Software, platforms, finance data, ERP-linked workflows, advisory-office tools, and digital service commercialization."],
    ["02", "Trade & Cross-Border Coordination", "Structured trade routes, representation models, sales coordination, and selected international business flows."],
    ["03", "Institutional & Public Modernization", "Digital government, donor-backed programs, modernization initiatives, and institutional transformation."],
    ["04", "Defense-Related Support & Localization", "Support services, maintenance environments, facility operations, security technologies, localization, and institutional operating contexts."],
    ["05", "Energy Transition & Infrastructure", "Selected opportunities around energy, infrastructure, project coordination, and local execution requirements."],
    ["06", "Mining & Natural Resources", "Opportunity mapping, regulatory awareness, partner evaluation, and structured entry thinking."],
  ];
  return (
    <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-32">
      <div className="mb-12 max-w-3xl">
        <MonoLabel>STRATEGIC OPPORTUNITY AREAS</MonoLabel>
        <h2 className="h-display mt-4 text-[34px] md:text-[44px]">
          Where insight, coordination, and infrastructure <Em>create practical value.</Em>
        </h2>
        <p className="mt-6 text-[14px] leading-[1.8] text-white/60">
          Forealco focuses on opportunity areas where market insight, local coordination,
          institutional understanding, and business infrastructure can create practical value.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {sectors.map(([n, t, d]) => (
          <Card key={n} className="p-6 group">
            <MonoLabel>{n}</MonoLabel>
            <div className="mt-3 font-display text-[22px] leading-[1.25]">{t}</div>
            <p className="mt-3 text-[12px] leading-[1.7] text-white/60">{d}</p>
            <div className="mt-5 h-px bg-gradient-to-r from-[#9DB4FF]/60 to-transparent" />
          </Card>
        ))}
      </div>
      <p className="mt-8 text-[12px] text-white/45 max-w-2xl">
        Forealco does not present these areas as generic sector promises. Each opportunity is assessed
        through structure, feasibility, local fit, partner readiness, institutional context, and
        execution logic.
      </p>
    </section>
  );
}

/* === 5 · CURATED NETWORK (premium rewrite) === */
function NetworkPreview() {
  const items = [
    { t: "Selected Advisory Environment", d: "Focused support aligned with the needs of each opportunity.", Icon: Gem },
    { t: "Erenown Ecosystem Support", d: "Specialized support layers through the wider brand environment where relevant.", Icon: Layers },
    { t: "Structured Execution Path", d: "A practical route from opportunity assessment to implementation.", Icon: Route },
  ];
  return (
    <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-32">
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-5">
          <MonoLabel>NETWORK</MonoLabel>
          <h2 className="h-display mt-5 text-[34px] md:text-[42px]">
            A curated network <Em>for structured execution.</Em>
          </h2>
          <p className="mt-7 text-[14px] leading-[1.85] text-white/70 max-w-md">
            Forealco works through a curated advisory and execution environment designed around
            quality, discretion, relevance, and execution discipline.
          </p>
          <p className="mt-5 text-[13px] leading-[1.85] text-white/55 max-w-md">
            Where relevant, Forealco may coordinate with specialized brands within the wider
            Erenown ecosystem, including Hubinall and Hangonin, to support digital workflows,
            advisory-office tools, local presence, and practical operational needs.
          </p>
          <Link to="/network" className="btn-secondary mt-8">See Our Network →</Link>
        </div>
        <div className="col-span-12 lg:col-span-7 grid grid-cols-1 gap-3">
          {items.map(({ t, d, Icon }, i) => (
            <Card key={t} className="p-6 flex items-start gap-5">
              <div className="shrink-0 w-11 h-11 border border-[#9DB4FF]/40 flex items-center justify-center text-[#9DB4FF]">
                <Icon size={18} strokeWidth={1.4} />
              </div>
              <div className="flex-1">
                <span className="font-mono text-[10px] tracking-[0.3em] text-[#9DB4FF]">0{i + 1}</span>
                <div className="font-display text-[22px] mt-1">{t}</div>
                <div className="mt-2 text-[13px] text-white/65 leading-[1.7]">{d}</div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

/* === 6 · EXPERIENCE THAT UNDERSTANDS STRUCTURE === */
function ExperienceSection() {
  return (
    <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-32">
      <div className="border border-white/10 bg-white/[0.02] p-8 md:p-12 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-4">
          <MonoLabel>INSTITUTIONAL EXPERIENCE</MonoLabel>
          <h2 className="h-display mt-5 text-[30px] md:text-[36px]">
            Experience that <Em>understands structure.</Em>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-8 space-y-5 text-[14px] leading-[1.85] text-white/70">
          <p>
            Forealco's approach is shaped by senior-level experience in international contracting,
            regional operations, institutional environments, and cross-border business development.
          </p>
          <p>
            This includes extensive operational leadership experience in Kosovo's international
            environment, covering large-scale maintenance, support, infrastructure coordination,
            and multi-layered service delivery connected to NATO/KFOR-related facilities and
            operational requirements.
          </p>
          <p className="text-white/55">
            This background gives Forealco a practical understanding of structure, reliability,
            local execution, and institutional context.
          </p>
        </div>
      </div>
    </section>
  );
}

/* === 7 · INSIGHTS PREVIEW === */
function InsightsPreview() {
  return (
    <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-32">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <MonoLabel>INSIGHTS</MonoLabel>
          <h2 className="h-display mt-4 text-[34px] md:text-[44px] max-w-3xl">
            Strategic perspectives on <Em>market entry and opportunity development.</Em>
          </h2>
        </div>
        <Link to="/insights" data-testid="home-insights-all" className="btn-secondary">
          All Insights →
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {INSIGHTS.map(({ n, slug, title, dek }) => (
          <Link
            key={n}
            to={`/insights/${slug}`}
            data-testid={`home-insight-${n}`}
            className="block"
          >
            <Card className="p-6 h-full group cursor-pointer">
              <MonoLabel>NOTE · {n}</MonoLabel>
              <div className="mt-4 font-display text-[20px] leading-[1.3]">{title}</div>
              <p className="mt-3 text-[12px] leading-[1.7] text-white/55">{dek}</p>
              <div className="mt-6 text-[10px] tracking-[0.3em] uppercase text-[#9DB4FF] opacity-80 group-hover:opacity-100 transition-opacity">
                Read insight →
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
}

/* === 8 · FINAL CTA === */
function FinalCTA() {
  return (
    <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-32">
      <div className="border border-white/10 bg-white/[0.025] p-10 md:p-16 text-center">
        <MonoLabel>NEXT STEP</MonoLabel>
        <h2 className="h-display mt-5 text-[34px] md:text-[52px] max-w-3xl mx-auto">
          Have an opportunity <Em>to explore?</Em>
        </h2>
        <p className="mt-6 max-w-2xl mx-auto text-[14px] leading-[1.85] text-white/65">
          Whether you are evaluating Kosovo as a first step, considering a cross-border business route,
          or looking for a structured way to enter a developing market, Forealco can help frame the
          opportunity, test the route, and define the next practical move.
        </p>
        <div className="mt-10 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/explore-opportunities" data-testid="final-cta" className="btn-primary">
            Explore Opportunities →
          </Link>
          <Link to="/about" className="btn-secondary">About Forealco</Link>
        </div>
      </div>
    </section>
  );
}

// end of Home
